<!-- src/components/NavBar.vue -->
<template>
    <nav class="navbar">
        <div class="navbar-container">
            <!-- 汉堡菜单按钮 (仅移动端显示) -->
            <button class="hamburger-btn" @click="toggleMobileMenu" v-if="isMobile">
                <i class="fas fa-bars"></i>
            </button>

            <router-link to="/dashboard" class="logo">
                <i class="fas fa-book"></i>
                <span>图书管理系统</span>
            </router-link>

            <!-- 移动端导航菜单 (全屏覆盖) -->
            <transition name="slide">
                <div class="mobile-nav" v-if="showMobileMenu && isMobile" @click.self="closeMobileMenu">
                    <div class="mobile-nav-content">
                        <div class="close-btn" @click="closeMobileMenu">
                            <i class="fas fa-times"></i>
                        </div>

                        <div class="mobile-nav-links">
                            <router-link to="/dashboard" @click="closeMobileMenu">仪表盘</router-link>
                            <router-link to="/books" @click="closeMobileMenu">图书大全</router-link>
                            <router-link to="/borrow" @click="closeMobileMenu">我的借阅</router-link>
                            <router-link to="/seat-reservation" @click="closeMobileMenu">座位预约</router-link>
                            <router-link to="/api-docs" @click="closeMobileMenu">API文档</router-link>
                            <router-link to="/ticket" class="future-feature" @click="closeMobileMenu">问题反馈</router-link>
                        </div>

                        <div class="mobile-user-info" v-if="user">
                            <div class="user-avatar">
                                <i class="fas fa-user-circle"></i>
                            </div>
                            <div class="user-details">
                                <div class="user-name">
                                    {{ user.nickname || randomAnonName }}
                                </div>
                                <div class="user-email">{{ user.email }}</div>
                                <div v-if="isAdmin" class="admin-badge">管理员</div>
                            </div>
                            <button class="logout-btn" @click="logout">
                                <i class="fas fa-sign-out-alt"></i> 退出登录
                            </button>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- 桌面端导航链接 -->
            <div class="nav-links" v-if="!isMobile">
                <router-link to="/dashboard">仪表盘</router-link>
                <router-link to="/books">图书大全</router-link>
                <router-link to="/borrow">我的借阅</router-link>
                <router-link to="/seat-reservation">座位预约</router-link>
                <router-link to="/api-docs">API文档</router-link>
                <router-link to="/ticket" class="future-feature">问题反馈</router-link>
            </div>

            <div class="user-section" v-if="!isMobile && user">
                <MessageCenter />
                <div class="user-info">
                    <i class="fas fa-user-circle"></i>
                    <span>{{ user.email }}</span>
                    <span v-if="isAdmin" class="admin-badge">管理员</span>
                </div>
                <button class="logout-btn" @click="logout">
                    <i class="fas fa-sign-out-alt"></i> 退出
                </button>
            </div>

            <!-- 移动端用户图标和通知中心 -->
            <div class="mobile-icons" v-if="isMobile && user">
                <MessageCenter />
                <div class="user-avatar" @click="toggleUserDropdown">
                    <i class="fas fa-user-circle"></i>
                </div>
            </div>

            <!-- 移动端用户下拉菜单 -->
            <transition name="fade">
                <div class="user-dropdown" v-if="showUserDropdown && isMobile" v-click-outside="closeUserDropdown">
                    <div class="user-dropdown-header">
                        <div class="user-avatar">
                            <i class="fas fa-user-circle"></i>
                        </div>
                        <div class="user-details">
                            <div class="user-name">用户昵称</div>
                            <div class="user-email">{{ user.email }}</div>
                        </div>
                    </div>
                    <div class="dropdown-divider"></div>
                    <button class="dropdown-item logout-btn" @click="logout">
                        <i class="fas fa-sign-out-alt"></i> 退出登录
                    </button>
                </div>
            </transition>
        </div>
    </nav>
</template>

<script>
    import { computed, onMounted, ref, onUnmounted } from 'vue'
    import { useStore } from 'vuex'
    import { useRouter } from 'vue-router'
    import MessageCenter from '@/components/MessageCenter.vue'

    // 点击外部关闭下拉菜单的指令
    const clickOutside = {
        beforeMount(el, binding) {
            el.clickOutsideEvent = (event) => {
                if (!(el === event.target || el.contains(event.target))) {
                    binding.value();
                }
            };
            document.addEventListener('click', el.clickOutsideEvent);
        },
        unmounted(el) {
            document.removeEventListener('click', el.clickOutsideEvent);
        }
    };

    export default {
        components: {
            MessageCenter
        },
        directives: {
            'click-outside': clickOutside
        },
        setup() {
            const store = useStore()
            const router = useRouter()
            const isMobile = ref(false)
            const showMobileMenu = ref(false)
            const showUserDropdown = ref(false)

            const user = computed(() => store.state.user)
            const isAdmin = computed(() => store.getters.isAdmin)

            const randomAnonName = ['无名书侠', '隐姓埋名的大佬', '正在取名的旅人', '神秘访客', '书中幽灵'][Math.floor(Math.random() * 5)];

            // 检测移动端
            const checkMobile = () => {
                isMobile.value = window.innerWidth <= 768
            }

            const toggleMobileMenu = () => {
                showMobileMenu.value = !showMobileMenu.value
                // 关闭用户下拉菜单当打开移动菜单时
                if (showMobileMenu.value) {
                    showUserDropdown.value = false
                }
            }

            const closeMobileMenu = () => {
                showMobileMenu.value = false
            }

            const toggleUserDropdown = () => {
                showUserDropdown.value = !showUserDropdown.value
                // 关闭移动菜单当打开用户下拉时
                if (showUserDropdown.value) {
                    showMobileMenu.value = false
                }
            }

            const closeUserDropdown = () => {
                showUserDropdown.value = false
            }

            const logout = () => {
                store.commit('clearUser')
                router.push('/login')
                // 关闭所有打开的菜单
                showMobileMenu.value = false
                showUserDropdown.value = false
            }

            onMounted(async () => {
                await store.dispatch('initstore');
                store.dispatch('fetchMessages');

                // 初始检测
                checkMobile()
                // 监听窗口大小变化
                window.addEventListener('resize', checkMobile)
            });

            onUnmounted(() => {
                window.removeEventListener('resize', checkMobile)
            });

            return {
                user,
                isAdmin,
                logout,
                isMobile,
                showMobileMenu,
                showUserDropdown,
                toggleMobileMenu,
                closeMobileMenu,
                toggleUserDropdown,
                closeUserDropdown,
                randomAnonName
            }
        }
    }
</script>

<style scoped>
    .navbar {
        background: linear-gradient(135deg, #2c3e50 0%, #1a2530 100%);
        color: white;
        padding: 0 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        position: relative;
/*        z-index: 1000;*/
    }

    .navbar-container {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 0;
        position: relative;
    }

    .logo {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: white;
        font-size: 1.5rem;
        font-weight: bold;
/*        z-index: 10;*/
    }

        .logo i {
            margin-right: 10px;
            font-size: 1.8rem;
        }

    .nav-links {
        display: flex;
        gap: 25px;
    }

        .nav-links a {
            color: #ecf0f1;
            text-decoration: none;
            padding: 8px 12px;
            border-radius: 4px;
            transition: background-color 0.3s;
            font-weight: 500;
        }

            .nav-links a:hover {
                background-color: rgba(255, 255, 255, 0.1);
            }

            .nav-links a.router-link-exact-active {
                background-color: rgba(52, 152, 219, 0.2);
                color: #3498db;
            }

    .user-section {
        display: flex;
        align-items: center;
        gap: 15px;
    }

    .user-info {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(255, 255, 255, 0.1);
        padding: 8px 15px;
        border-radius: 20px;
    }

    .admin-badge {
        background-color: #e74c3c;
        color: white;
        font-size: 0.75rem;
        padding: 2px 8px;
        border-radius: 10px;
        margin-left: 5px;
        display: inline-block;
    }

    .logout-btn {
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 8px 15px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;
        display: flex;
        align-items: center;
        gap: 5px;
    }

        .logout-btn:hover {
            background-color: rgba(231, 76, 60, 0.2);
            border-color: #e74c3c;
        }

    .future-feature {
        position: relative;
        color: #f39c12 !important;
    }

        .future-feature::before {
            content: "beta";
            position: absolute;
            top: -8px;
            right: -10px;
            background: #f39c12;
            color: white;
            font-size: 0.7rem;
            padding: 2px 5px;
            border-radius: 3px;
        }

    /* 在导航栏样式中添加链接过渡效果 */
    .nav-links a,
    .mobile-nav-links a {
        position: relative;
        transition: all 0.3s ease;
    }

        /* 桌面端链接下划线效果 */
        .nav-links a:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            width: 0;
            height: 2px;
            background: #3498db;
            transition: all 0.3s ease;
            transform: translateX(-50%);
        }

        .nav-links a:hover:after,
        .nav-links a.router-link-exact-active:after {
            width: 70%;
        }

    /* 移动端链接背景变化效果 */
    .mobile-nav-links a {
        transition: background-color 0.3s ease, transform 0.2s ease;
    }

        .mobile-nav-links a:active {
            transform: scale(0.98);
        }

    /* 移动端样式 */
    @media (max-width: 768px) {
        .navbar-container {
            padding: 10px 0;
        }

        .logo span {
            font-size: 1.3rem;
        }

        .nav-links,
        .user-section {
            display: none;
        }

        .hamburger-btn {
            background: transparent;
            border: none;
            color: white;
            font-size: 1.5rem;
            cursor: pointer;
            padding: 5px 10px;
            margin-right: 10px;
/*            z-index: 1001;*/
        }

        .mobile-icons {
            display: flex;
            align-items: center;
            gap: 15px;
/*            z-index: 10;*/
        }

        .user-avatar {
            font-size: 1.8rem;
            cursor: pointer;
            display: flex;
            align-items: center;
        }

        /* 移动端导航菜单 */
        .mobile-nav {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            z-index: 999;
            display: flex;
        }

        .mobile-nav-content {
            width: 85%;
            max-width: 350px;
            background: #2c3e50;
            height: 100%;
            padding: 20px;
            overflow-y: auto;
            position: relative;
        }

        .close-btn {
            position: absolute;
            top: 15px;
            right: 15px;
            font-size: 1.5rem;
            cursor: pointer;
        }

        .mobile-nav-links {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 50px;
        }

            .mobile-nav-links a {
                color: white;
                text-decoration: none;
                padding: 12px 15px;
                border-radius: 5px;
                background: rgba(255, 255, 255, 0.1);
                font-size: 1.1rem;
            }

        .mobile-user-info {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.2);
        }

        .user-details {
            margin: 15px 0;
        }

        .user-name {
            font-weight: bold;
            font-size: 1.2rem;
        }

        .user-email {
            font-size: 0.9rem;
            opacity: 0.8;
            margin: 5px 0;
        }

        /* 用户下拉菜单 */
        .user-dropdown {
            position: absolute;
            top: 100%;
            right: 15px;
            background: #2c3e50;
            border-radius: 8px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
            width: 280px;
/*            z-index: 1000;*/
            overflow: hidden;
        }

        .user-dropdown-header {
            display: flex;
            align-items: center;
            padding: 15px;
            gap: 12px;
        }

        .dropdown-divider {
            height: 1px;
            background: rgba(255, 255, 255, 0.1);
        }

        .dropdown-item {
            width: 100%;
            text-align: left;
            padding: 12px 20px;
            background: transparent;
            border: none;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 1rem;
        }

            .dropdown-item:hover {
                background: rgba(255, 255, 255, 0.1);
            }
    }

    /* 桌面端样式 */
    @media (min-width: 769px) {
        .hamburger-btn,
        .mobile-icons,
        .mobile-nav,
        .user-dropdown {
            display: none;
        }
    }

    /* 过渡动画 */
    .slide-enter-active, .slide-leave-active {
        transition: transform 0.4s ease, opacity 0.3s ease;
    }

    .slide-enter-from, .slide-leave-to {
        transform: translateX(-100%);
        opacity: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.3s ease, transform 0.3s ease;
    }

    .fade-enter-from, .fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
</style>